import React, { useMemo, useCallback } from 'react'
import { Checkbox } from 'antd'
import { useAppDispatch, useAppSelector } from '../../../../../app/hooks'
import actionTypes from '../../../../../constants/actionTypes'
import { formatNum } from '../../../../../utils/utils'
import { useAddPhotosTrack } from '../hooks/useAddPhotosTrack'
import { useTeamPhotoPickerCtx } from '../context'
import './HeaderExtra.scss'

interface HeaderExtraProps {
  sameWatermarkOnly?: boolean
  currentWatermarkKey?: string | null
  onToggleSameWatermark?: (checked: boolean) => void
}

const HeaderExtra: React.FC<HeaderExtraProps> = ({
  sameWatermarkOnly = false,
  currentWatermarkKey,
  onToggleSameWatermark
}) => {
  const dispatch = useAppDispatch()
  const photos = useAppSelector((state) => state.photo.photos)
  const checkAll = useAppSelector((state) => state.photo.checkAll)
  const checkedPhotos = useAppSelector((state) => state.photo.checkedPhotos)
  const unCheckedPhotos = useAppSelector((state) => state.photo.unCheckedPhotos)
  const filter = useAppSelector((state) => state.photo.filter)
  // 已选照片来源:aiClerk 流走 Context.excludePhotos,batch-editor 流走 redux
  const { excludePhotos, relaxFilter } = useTeamPhotoPickerCtx()
  const photoPickerPhotos = useAppSelector((state) => {
    if (state.photoPicker.from === 'batch-editor') {
      return state.photoPicker.photos
    }
    return []
  })
  const selectedPhotosFromPicker = useMemo(
    () => (excludePhotos.length > 0 ? excludePhotos : photoPickerPhotos),
    [excludePhotos, photoPickerPhotos]
  )

  // 使用埋点 hook
  const { trackAddPhotos } = useAddPhotosTrack()

  // 检查照片是否有防伪码
  const hasAntiFakeCode = useCallback((photo: any): boolean => {
    try {
      const extension = photo.extension
      if (!extension) {
        return false
      }
      const extensionObj =
        typeof extension === 'string' ? JSON.parse(extension) : extension
      const antiFakeCode = extensionObj?.antiFakeCode
      return antiFakeCode && antiFakeCode.trim().length > 0
    } catch (error) {
      console.error('解析 extension 失败:', error)
      return false
    }
  }, [])

  // 过滤照片
  const filteredPhotos = useMemo(() => {
    if (!photos || photos.length === 0) {
      return photos
    }
    if (relaxFilter) return photos
    const selectedSmallURLs = new Set(
      selectedPhotosFromPicker.map((p: any) => p.smallURL)
    )
    return photos.filter((photo: any) => {
      const hasWatermark = photo.hasWatermark === true
      const isImage = photo.mediaType === 0
      const notSelected = !selectedSmallURLs.has(photo.smallURL)
      const photoWatermarkBaseID = photo.watermarkBaseID
      const isNotWatermarkBaseID21 = photoWatermarkBaseID !== '21'
      const hasAntiFake = hasAntiFakeCode(photo)
      return (
        hasWatermark &&
        isImage &&
        notSelected &&
        isNotWatermarkBaseID21 &&
        hasAntiFake
      )
    })
  }, [photos, selectedPhotosFromPicker, hasAntiFakeCode, relaxFilter])

  // 计算筛选后的选中数量
  const selectedCount = useMemo(() => {
    if (checkAll) {
      const unCheckedSeqSet = new Set(Object.keys(unCheckedPhotos).map(Number))
      return filteredPhotos.filter((p: any) => !unCheckedSeqSet.has(p.seq))
        .length
    } else {
      const checkedSeqSet = new Set(Object.keys(checkedPhotos).map(Number))
      return filteredPhotos.filter((p: any) => checkedSeqSet.has(p.seq)).length
    }
  }, [checkAll, filteredPhotos, checkedPhotos, unCheckedPhotos])

  // 全选
  const onCheckAll = (e: any) => {
    const checked = e.target.checked

    const searchKeywords = filter.searchKeywords
    trackAddPhotos({
      actionType: checked ? 'chooseAll' : 'unChooseAll',
      searchWord:
        searchKeywords && searchKeywords.length > 0
          ? searchKeywords[0] || ''
          : ''
    })

    dispatch({
      type: actionTypes.SET_CHECK_ALL,
      data: checked
    })
    dispatch({ type: actionTypes.CLEAR_CHECKED_PHOTO })
    dispatch({ type: actionTypes.CLEAR_UNCHECKED_PHOTO })
  }

  return (
    <div className="add-batch-header-extra">
      <div className="add-batch-header-extra__left">
        <Checkbox
          disabled={filteredPhotos.length === 0}
          indeterminate={
            (checkAll &&
              filteredPhotos.some((p: any) => p.seq in unCheckedPhotos)) ||
            (!checkAll &&
              filteredPhotos.some((p: any) => p.seq in checkedPhotos))
          }
          checked={
            checkAll &&
            filteredPhotos.length > 0 &&
            filteredPhotos.every((p: any) => !(p.seq in unCheckedPhotos))
          }
          onChange={onCheckAll}
        >
          全选
        </Checkbox>
        <span className="add-batch-header-extra__info">
          已选择{' '}
          <strong className="add-batch-header-extra__info-count">
            {formatNum(selectedCount)}
          </strong>{' '}
          张 / 共{' '}
          <strong className="add-batch-header-extra__info-count">
            {formatNum(filteredPhotos.length)}
          </strong>{' '}
          张
        </span>
      </div>
      <div className="add-batch-header-extra__right">
        {currentWatermarkKey ? (
          <Checkbox
            checked={sameWatermarkOnly}
            onChange={(e) => onToggleSameWatermark?.(e.target.checked)}
          >
            添加相同水印
          </Checkbox>
        ) : null}
      </div>
    </div>
  )
}

export default HeaderExtra
